import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAnalytics, logEvent, Analytics, isSupported } from 'firebase/analytics';
import { initAuth } from '../services/auth/authService';

// Firebase configuration - uses environment variables
// Defaults to dev/UAT config if not set
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "AIzaSyDWAcy1tv6zIUtK-l5vwCC4WGAsHlGYnNk",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "dayli-cards-dev.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "dayli-cards-dev",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "dayli-cards-dev.firebasestorage.app",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "713949538701",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:713949538701:web:1aadcfa7bc674e56cc75c4",
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || "G-Q4HJKDT6QK"
};

// Initialize Firebase
let app: FirebaseApp | null = null;
let analytics: Analytics | null = null;
let isInitializing = false;
let isInitialized = false;
let analyticsReadyPromise: Promise<void> | null = null;
let analyticsReadyResolve: (() => void) | null = null;

// Create a promise that resolves when analytics is ready
const createAnalyticsReadyPromise = () => {
  if (!analyticsReadyPromise) {
    analyticsReadyPromise = new Promise<void>((resolve) => {
      analyticsReadyResolve = resolve;
    });
  }
  return analyticsReadyPromise;
};

export const initFirebase = async () => {
  // If already initialized, return immediately
  if (isInitialized) {
    return;
  }
  
  // If already initializing, wait for the promise
  if (isInitializing) {
    if (analyticsReadyPromise) {
      await analyticsReadyPromise;
    }
    return;
  }
  
  isInitializing = true;
  createAnalyticsReadyPromise(); // Create the promise before initialization
  
  try {
    console.log('Initializing Firebase...');
    
    // Initialize Firebase app (shared for analytics and auth)
    app = initializeApp(firebaseConfig);
    
    // Initialize Analytics
    const supported = await isSupported();
    console.log('Analytics supported:', supported);
    
    if (supported) {
      analytics = getAnalytics(app);
      console.log('Analytics initialized successfully');
      // Resolve the promise when analytics is ready
      if (analyticsReadyResolve) {
        analyticsReadyResolve();
        analyticsReadyResolve = null;
      }
    } else {
      console.warn('Analytics not supported in this environment');
      // Still resolve even if not supported, so tracking calls don't hang
      if (analyticsReadyResolve) {
        analyticsReadyResolve();
        analyticsReadyResolve = null;
      }
    }
    
    // Initialize Auth (for API protection)
    await initAuth(app);
    
    isInitialized = true;
    console.log('Firebase initialized successfully');
  } catch (error) {
    console.error('Firebase init error:', error);
    // Resolve on error too, so tracking calls don't hang
    if (analyticsReadyResolve) {
      analyticsReadyResolve();
      analyticsReadyResolve = null;
    }
  } finally {
    isInitializing = false;
  }
};

/**
 * Wait for analytics to be initialized
 * Returns a promise that resolves when analytics is ready (or if not supported)
 */
export const waitForAnalytics = async (): Promise<boolean> => {
  // If already initialized and analytics exists, return immediately
  if (analytics) {
    return true;
  }
  
  // If not initialized yet, start initialization
  if (!isInitialized && !isInitializing) {
    await initFirebase();
  }
  
  // Wait for the analytics ready promise
  if (analyticsReadyPromise) {
    await analyticsReadyPromise;
  }
  
  // Return true if analytics is available, false otherwise
  return !!analytics;
};

// Event Names - Exact camelCase as specified
export const EVENTS = {
  // Page Views
  bankSelectionPageView: 'bankSelectionPageView',
  accountSelectionPageView: 'accountSelectionPageView',
  errorPageView: 'errorPageView',
  thankyouMessage: 'thankyouMessage',
  
  // Bank Selection
  searchInitiated: 'searchInitiated',
  topBankSelected: 'topBankSelected',
  chimeIsPrimaryBankAgreed: 'chimeIsPrimaryBankAgreed',
  chimeIsPrimaryBankDisagreed: 'chimeIsPrimaryBankDisagreed',
  searchResultsBankSelected: 'searchResultsBankSelected',
  
  // Finicity Flow
  controlBackToDayli: 'controlBackToDayli',
  
  // Account Selection
  accountsUncheckedAction: 'accountsUncheckedAction',
  accountsCheckedAction: 'accountsCheckedAction',
  saveCtaClicked: 'saveCtaClicked',
};

/**
 * Track analytics event
 * 
 * Note: GA4 does not natively support arrays as parameter values.
 * Arrays/lists must be converted to comma-separated strings before calling this function.
 * Example: accountIds: ['id1', 'id2'] -> accountIds: 'id1,id2'
 * 
 * @param eventName - The name of the event to track
 * @param attributes - Event parameters (arrays should be stringified as comma-separated strings)
 */
export const track = async (eventName: string, attributes?: Record<string, any>) => {
  console.log('Track called:', eventName, attributes);
  
  // Wait for analytics to be ready
  const analyticsAvailable = await waitForAnalytics();
  
  if (!analyticsAvailable || !analytics) {
    console.warn('Analytics not available, cannot track:', eventName);
    return;
  }
  
  try {
    // Always include productId: 'dayli' in all events (cannot be overridden)
    const eventAttributes = {
      ...attributes,
      productId: 'dayli',
    };
    
    logEvent(analytics, eventName, eventAttributes);
    console.log('Event tracked:', eventName, eventAttributes);
  } catch (error) {
    console.error('Track error:', error);
  }
};

